/**
 * Which kind of work a grid is showing, when it holds more than one.
 *
 * A profile's Capsules tab draws two kinds of thing: the capsules an account has
 * made, and the **modules** it has made out of them. They belong together — a
 * module is a way of arranging capsules, not a separate shelf — but they are not
 * the same object, and a reader wants to be able to say which they mean. So the tab
 * keeps one grid and gains one row of choices above it: *All · Capsules · Modules*.
 *
 * Three decisions:
 *
 *  - **Counts, on every choice.** The row is the only place that says how much of
 *    each thing there is, and a chooser whose options are all the same length (an
 *    empty tab, a tab with a hundred rows) teaches nothing. A choice with a zero is
 *    still drawn: on your own page `Modules 0` is how a creator finds out the
 *    feature exists, and the row keeps its shape as work arrives. A *visitor* is
 *    shown the row only once there is something in it, which is the caller's
 *    decision and not this component's.
 *  - **It is one row, always visible, and it does not scroll away.** Paging a tab
 *    inside a tab — two tab strips on one screen — is the thing this avoids: the
 *    profile's own strip is the navigation, and this row is a filter on the grid
 *    under it. It scrolls sideways rather than wrapping, like every other strip of
 *    chips in the app, because a filter taller than the grid it filters is not a
 *    filter.
 *  - **`All` is first and is the default.** The tab's question is *what has this
 *    account made*, and both answers belong to it. A reader who wants only one says
 *    so; a reader who does not should not have to choose at all.
 *
 * A tab that holds a single kind never carries it — the Flashbooks tab is books,
 * and there is nothing to choose between — and a profile with no modules at all
 * carries it only on its owner's own page, where it is a door rather than a filter.
 * Both decisions are the caller's (see `#/screens/Profile/Sections/Work`).
 */
import {ScrollView, View} from 'react-native'
import {useLingui} from '@lingui/react/macro'

import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Text} from '#/components/Typography'
import {type WorkKind} from './types'

/** What the grid is filtered to: everything in the tab, or one kind. */
export type WorkKindFilter = 'all' | WorkKind

export function WorkKindSwitch({
  value,
  onChange,
  counts,
  label,
  testIDPrefix,
}: {
  value: WorkKindFilter
  onChange: (value: WorkKindFilter) => void
  /** How many of each kind the tab holds, so each choice can say. */
  counts: Partial<Record<WorkKind, number>>
  /** What the strip is filtering, said once for a reader who cannot see counts. */
  label: string
  testIDPrefix: string
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  /*
   * Both choices are always drawn, in the app's own order: capsules first, then
   * the thing made *of* them.
   *
   * Including the ones there are none of. A choice that vanished when the count
   * reached zero would be the row changing shape under a reader's hand — and worse,
   * it is exactly the zero case that matters: `Modules 0` on your own page is how a
   * creator discovers that a module is a thing they can make (see the caller).
   */
  const kinds: WorkKind[] = ['capsule', 'module']
  const total = kinds.reduce((sum, kind) => sum + (counts[kind] ?? 0), 0)

  return (
    <View
      testID={`${testIDPrefix}Switch`}
      style={[a.px_lg, a.pt_md, a.pb_sm, a.gap_xs]}>
      <Text style={[a.text_xs, a.font_semi_bold, t.atoms.text_contrast_medium]}>
        {label}
      </Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[a.flex_row, a.gap_xs]}>
        <Choice
          testID={`${testIDPrefix}Switch-all`}
          label={l`All ${total}`}
          selected={value === 'all'}
          onPress={() => onChange('all')}
        />
        {kinds.map(kind => (
          <Choice
            key={kind}
            testID={`${testIDPrefix}Switch-${kind}`}
            label={
              kind === 'capsule'
                ? l`Capsules ${counts.capsule ?? 0}`
                : kind === 'module'
                  ? l`Modules ${counts.module ?? 0}`
                  : l`Books ${counts.flashbook ?? 0}`
            }
            selected={value === kind}
            onPress={() => onChange(kind)}
          />
        ))}
      </ScrollView>
    </View>
  )
}

function Choice({
  testID,
  label,
  selected,
  onPress,
}: {
  testID: string
  label: string
  selected: boolean
  onPress: () => void
}) {
  return (
    <Button
      testID={testID}
      label={label}
      size="small"
      color={selected ? 'primary' : 'secondary'}
      shape="default"
      onPress={onPress}>
      <ButtonText>{label}</ButtonText>
    </Button>
  )
}
